/**
 *
 * UserQuestionnaire
 *
 */

import React, {
  memo,
  FunctionComponent,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';
import { useHistory, useRouteMatch } from 'react-router';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../../../app/store';
import QuestionnarieService, {
  calculateQuestionnaireResult,
  fillQuestionnaire,
} from '../../../../service/Questionnarie.service';
import { selectLanguage } from '../../../LanguageProvider/LanguageProvider.selectors';
import { Wrap } from '../../../../assets/styledAssets/grid';
import { StyledLoader } from '../../../../assets/styledAssets/loader';
import Button from '../../../../components/Button/Button.component';
import { BackIcon } from '../../../../components/Icons/Icons.component';
import QuestionnairesForm from '../../../Questionnaires/components/QuestionnairesForm/QuestionnairesForm.component';
import {
  setActiveQuestionnaire,
  editAnswer,
} from '../../../Questionnaires/Questionnaires.slice';
import { FilledAnswer } from '../../../Questionnaires/questionnaires';

interface MatchParams {
  questionId: string;
  requestId: string;
}

const connector = connect(
  (state: RootState) => ({
    selectedLanguage: selectLanguage(state),
    activeQuestionnaire: state.questionnaires.activeQuestionnaire,
  }),
  {
    setActiveQuestionnaire,
    editAnswer,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const UserQuestionnaire: FunctionComponent<PropsFromRedux> = ({
  selectedLanguage,
  activeQuestionnaire,
  setActiveQuestionnaire,
  editAnswer,
}) => {
  const intl = useIntl();
  const { params } = useRouteMatch<MatchParams>();
  const [countQuestions, setCountQuestions] = useState<number>(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<number>(0);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const history = useHistory();

  useEffect(() => {
    setShowLoader(true);
    QuestionnarieService.fetchQuestionnaire(
      params.questionId,
      selectedLanguage?.id as string,
    )
      .then((res) => {
        setActiveQuestionnaire(res);
      })
      .finally(() => setShowLoader(false));

    return () => {
      setActiveQuestionnaire(undefined);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let countQ = 0;
    let answeredQ = 0;
    if (activeQuestionnaire) {
      activeQuestionnaire.questionSeries.forEach((qSeria) => {
        qSeria.questions.forEach((question) => {
          countQ++;
          question.answers.forEach((answer) => {
            if (answer.selected) {
              answeredQ++;
            }
          });
        });
      });
      setCountQuestions(countQ);
      setAnsweredQuestions(answeredQ);
    }
  }, [activeQuestionnaire]);

  const sendAnswers = () => {
    const answers: FilledAnswer[] = [];
    if (activeQuestionnaire) {
      activeQuestionnaire.questionSeries.forEach((qSeria) => {
        qSeria.questions.forEach((question) => {
          question.answers.forEach((answer) => {
            if (answer.selected) {
              answers.push({
                questionId: question.id as string,
                answerId: answer.id as string,
                score: answer.score,
              });
            }
          });
        });
      });

      fillQuestionnaire(params.requestId, answers).then((res) => {
        calculateQuestionnaireResult(res.questionnaireFillId, true).then(() => {});
        history.push('/home');
      });
    }
  };

  const percentOfAnswered = useMemo(() => {
    return Math.round((answeredQuestions / countQuestions) * 100) || 0;
  }, [answeredQuestions, countQuestions]);

  return (
    <Wrap
      flexWrap={'nowrap'}
      flexDir={'column'}
      width={'100%'}
      alignItems={'center'}
      overflow={'hidden'}
    >
      <Wrap
        flexWrap={'nowrap'}
        width={'100%'}
        justifyCont={'space-between'}
      >
        <Wrap alignItems={['flex-start', 'center']} flexDir={['column', 'row']}>
          <Wrap margin={'0 -1rem'}>
            <Button
              icon
              to={'/home'}
              children={<BackIcon color={'#000'} width={'3rem'} />}
            />
          </Wrap>
          <Wrap
            width={['100%', '80%']}
            fs={'4rem'}
            fw={'bold'}
            color={'#000'}
            alignItems={'center'}
            justifyCont={['flex-start', 'center']}
            flexWrap={'nowrap'}
            padding={['0', '0 0 0 6rem']}
          >
            {activeQuestionnaire?.name}
          </Wrap>
        </Wrap>
        <Button primary onClick={sendAnswers}>
          {intl.formatMessage(messages.complete)}
        </Button>
      </Wrap>
      <Wrap
        margin={'0 0 3rem 0'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Wrap fs={'2rem'} color={'#000'} alignItems={'center'}>
          {intl.formatMessage(messages.qProgress)}
        </Wrap>
        <Wrap
          padding={'0 0 0 1rem'}
          fs={'2rem'}
          fw={'bold'}
          color={'#000'}
          alignItems={'center'}
        >
          {answeredQuestions} / {countQuestions} or {percentOfAnswered}%
        </Wrap>
      </Wrap>
      <Wrap maxWidth={['100%', '80%']} overflow={'auto'}>
        <QuestionnairesForm
          activeQuestionnaire={activeQuestionnaire}
          editAnswer={editAnswer}
        />
      </Wrap>
      {showLoader && <StyledLoader />}
    </Wrap>
  );
};

export default connector(memo(UserQuestionnaire));
